// Family sponsorship — the status math behind the admin "Sponsored families"
// screen, as defined by the `family_sponsorship` table in
// apps/portal/supabase/migrations/20260727000005_family_sponsorship.sql.
//
// WHAT A SPONSORSHIP IS. An organisation that already pays Zooylo can give a
// family it serves full app access without that family paying. The grant is
// keyed on the PARENT'S EMAIL — an adult email, never a child — and attaches to
// their account the first time they sign in.
//
// WHY THE STATUS IS DERIVED AND NOT STORED. Only two of the four states are ever
// written: `claimed_at` (the family signed in) and `revoked_at` (an admin ended
// it early). "Lapsed" needs no write at all — time does it. Deriving all four
// from the row means the portal, the mobile app and the SQL can never disagree
// about a row nobody has touched since it expired.
//
// `nowMs` is INJECTED everywhere. Nothing here reads the wall clock, so the demo
// path can evaluate a fixed fixture epoch and stay deterministic forever while
// the live path passes the real time.
//
// Pure TS, no dependencies.

/**
 * Where one grant is in its life.
 *
 *  - `pending`  created, but that email has not signed in yet.
 *  - `active`   claimed by an account and still inside its end date.
 *  - `lapsed`   the end date has passed. No write happened — time did it.
 *  - `revoked`  an admin ended it early. Terminal; the row is kept as audit.
 */
export type SponsorshipStatus = "pending" | "active" | "lapsed" | "revoked";

/**
 * One grant, as an admin may see it. Deliberately exhaustive: adult email plus
 * dates, and NOTHING about a child. Sponsorship is account-to-org; child linking
 * is adult-to-child. An admin must never gain a path to child identity or
 * progress through this shape.
 */
export interface Sponsorship {
  id: string;
  /** The sponsored adult's email address. Never a child reference. */
  email: string;
  /** ISO-8601 instant the grant stops working. */
  endsAt: string;
  /** ISO-8601 instant an admin created it. */
  grantedAt: string;
  /** ISO-8601 instant that email signed in and claimed it, else null. */
  claimedAt: string | null;
  /** ISO-8601 instant an admin ended it early, else null. */
  revokedAt: string | null;
}

/**
 * Parse an ISO instant, or `null` when it cannot be read.
 *
 * Fail-closed helper: every caller below treats `null` as "this grant is not
 * usable", so a corrupt date can only ever REMOVE access, never extend it.
 */
function parse(iso: string | null | undefined): number | null {
  if (typeof iso !== "string" || iso.trim() === "") return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The state of `s` at `nowMs`.
 *
 * Precedence, highest first:
 *   1. `revoked`  — `revokedAt` is set. An admin's decision outranks the clock,
 *                   and outranks whether the family ever claimed it.
 *   2. `lapsed`   — `endsAt <= nowMs`. The end date is EXCLUSIVE: at the instant
 *                   it is reached, access has ended. This is exactly the SQL's
 *                   `ends_at > now()`, and the same rule the mobile app applies
 *                   to its offline cache.
 *   3. `pending`  — never claimed (`claimedAt` is null).
 *   4. `active`   — claimed and still inside its window.
 *
 * FAILS CLOSED. An unparseable `endsAt`, or a `nowMs` that is not a finite
 * number (an unset device clock), yields `lapsed` — the state that grants
 * nothing. `revokedAt` is judged on PRESENCE, not parseability, so an unreadable
 * revocation timestamp still revokes.
 */
export function sponsorshipStatus(
  s: Sponsorship,
  nowMs: number,
): SponsorshipStatus {
  if (s.revokedAt) return "revoked";

  const endsAtMs = parse(s.endsAt);
  if (endsAtMs === null || !Number.isFinite(nowMs)) return "lapsed";
  if (endsAtMs <= nowMs) return "lapsed";

  return s.claimedAt ? "active" : "pending";
}

/**
 * Whether `s` occupies one of the organisation's `license.family_allowance`
 * slots at `nowMs` — i.e. `pending` or `active`.
 *
 * This MUST match what `grant_family_sponsorship` counts server-side:
 *
 *   where s.org_id = p_org and s.revoked_at is null and s.ends_at > now()
 *
 * which is precisely "not revoked and not yet lapsed", regardless of whether the
 * family has claimed it. A pending grant holds its slot: the seat is reserved
 * the moment the admin creates it, so the "N of M used" line on screen predicts
 * the server's answer rather than contradicting it.
 *
 * The one divergence is the fail-closed branch above: an unparseable `endsAt`
 * counts here as lapsed and so does NOT consume a slot, where the server would
 * still count the row. Postgres cannot produce that value (`ends_at` is a
 * `timestamptz not null`), so it is unreachable in practice.
 */
export function countsTowardAllowance(s: Sponsorship, nowMs: number): boolean {
  const status = sponsorshipStatus(s, nowMs);
  return status === "pending" || status === "active";
}
